import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from './Icon';

/*
 * Dropdown for a table row (or any trigger).
 *
 * - The popover is position:fixed, anchored to the trigger's rect, so a
 *   scrolling table (.pm-table-scroll) can't clip it. It flips above the
 *   trigger when there isn't room below, and closes on scroll/resize/Escape.
 * - Every click inside stops propagation: table rows open a detail view on
 *   click, and React bubbles events from children through the row.
 * - Items: { id, label, icon?, onSelect, disabledReason? } or
 *   { separator: true }. A disabled item stays visible with its reason
 *   underneath, so staff learn why instead of guessing.
 */
const RowMenu = ({ items, triggerClassName, triggerContent, triggerLabel, align = 'right' }) => {
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const close = useCallback(() => setRect(null), []);

  useEffect(() => {
    if (!rect) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    // Move focus into the menu for keyboard users. preventScroll: a focus
    // scroll would trip the close-on-scroll listener above.
    menuRef.current?.querySelector('.pm-menu-item:not(:disabled)')?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [rect, close]);

  const toggle = (e) => {
    e.stopPropagation();
    if (rect) {
      close();
      return;
    }
    setRect(triggerRef.current.getBoundingClientRect());
  };

  let menuStyle;
  if (rect) {
    const estimatedHeight = items.length * 46 + 10;
    const openUp = window.innerHeight - rect.bottom < estimatedHeight + 12 && rect.top > estimatedHeight;
    menuStyle = {
      position: 'fixed',
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
      ...(align === 'right'
        ? { right: Math.max(8, window.innerWidth - rect.right) }
        : { left: Math.max(8, rect.left) }),
    };
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={!!rect}
        aria-label={triggerLabel}
        title={triggerLabel}
        onClick={toggle}
      >
        {triggerContent}
      </button>
      {rect && (
        <div onClick={(e) => e.stopPropagation()}>
          <div className="pm-menu-backdrop" onClick={close} />
          <div ref={menuRef} role="menu" className="pm-menu" style={menuStyle}>
            {items.map((item, i) =>
              item.separator ? (
                <div key={`sep-${i}`} className="pm-menu-sep" role="separator" />
              ) : (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className="pm-menu-item"
                  disabled={!!item.disabledReason}
                  onClick={() => {
                    if (item.disabledReason) return;
                    close();
                    item.onSelect();
                  }}
                >
                  {item.icon && <Icon name={item.icon} size={15} />}
                  <span className="pm-menu-item-text">
                    <span>{item.label}</span>
                    {item.disabledReason && (
                      <span className="pm-menu-item-hint">{item.disabledReason}</span>
                    )}
                  </span>
                </button>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RowMenu;
